import { Suspense, lazy } from "react";
import Index from "@/pages/Index";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AnalyticsProvider } from "@/hooks/useAnalytics";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Lazy load page components
const About = lazy(() => import("./pages/About"));
const Resources = lazy(() => import("./pages/Resources"));
const ResourceDetail = lazy(() => import("./pages/ResourceDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const EndUserTerms = lazy(() => import("./pages/EndUserTerms"));
const LeasingAddendum = lazy(() => import("./pages/LeasingAddendum"));
const CameraAIAddendum = lazy(() => import("./pages/CameraAIAddendum"));
const AlarmFireAddendum = lazy(() => import("./pages/AlarmFireAddendum"));
const Reschedule = lazy(() => import("./pages/Reschedule"));
const BecomePartner = lazy(() => import("./pages/BecomePartner"));
const CustomerQuoteView = lazy(() => import("./pages/CustomerQuoteView"));
const SignAgreement = lazy(() => import("./pages/SignAgreement"));
const AIQuote = lazy(() => import("./pages/AIQuote"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Template = lazy(() => import("./pages/Template"));
const Services = lazy(() => import("./pages/Services"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const AdminAIQuotes = lazy(() => import("./pages/AdminAIQuotes").then(m => ({ default: m.AdminAIQuotes })));
const AdminAIQuoteDetail = lazy(() => import("./pages/AdminAIQuotes").then(m => ({ default: m.AdminAIQuoteDetail })));

// Admin
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminContacts = lazy(() => import("./pages/AdminContacts"));
const AdminResources = lazy(() => import("./pages/AdminResources"));
const AdminResourceEditor = lazy(() => import("./pages/AdminResourceEditor"));
const ContactDetail = lazy(() => import("./pages/ContactDetail"));
const AppointmentsCalendar = lazy(() => import("./pages/AppointmentsCalendar"));
const AppointmentsList = lazy(() => import("./pages/AppointmentsList"));
const AdminCalendar = lazy(() => import("./pages/AdminCalendar"));
const AppointmentDetail = lazy(() => import("./pages/AppointmentDetail"));
const Activities = lazy(() => import("./pages/Activities"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const UserApprovals = lazy(() => import("./pages/UserApprovals"));
const AdminQuotes = lazy(() => import("./pages/AdminQuotes"));
const AdminQuoteBuilder = lazy(() => import("./pages/AdminQuoteBuilder"));
const AdminQuoteDetail = lazy(() => import("./pages/AdminQuoteDetail"));
const AdminProducts = lazy(() => import("./pages/AdminProducts"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminChat = lazy(() => import("./pages/AdminChat"));
const AdminAgreements = lazy(() => import("./pages/AdminAgreements"));
const AdminTasks = lazy(() => import("./pages/AdminTasks"));
const AdminResellerOrgs = lazy(() => import("./pages/AdminResellerOrgs"));
const AdminResellerOrgDetail = lazy(() => import("./pages/AdminResellerOrgDetail"));
const AdminTradeMode = lazy(() => import("./pages/AdminTradeMode"));
const AdminOnboarding = lazy(() => import("./pages/AdminOnboarding"));
const AdminAIFeedback = lazy(() => import("./pages/AdminAIFeedback"));
const AdminOrderForm = lazy(() => import("./pages/AdminOrderForm"));
const AdminAIAssistant = lazy(() => import("./pages/AdminAIAssistant"));

// Reseller portal
const PortalLogin = lazy(() => import("./pages/portal/PortalLogin"));
const PortalDashboard = lazy(() => import("./pages/portal/PortalDashboard"));
const PortalOrganization = lazy(() => import("./pages/portal/PortalOrganization"));
const PortalLocations = lazy(() => import("./pages/portal/PortalLocations"));
const PortalLines = lazy(() => import("./pages/portal/PortalLines"));
const PortalTickets = lazy(() => import("./pages/portal/PortalTickets"));
const PortalUsers = lazy(() => import("./pages/portal/PortalUsers"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AnalyticsProvider>
          <ScrollToTop />
          <ErrorBoundary>
            <Suspense
              fallback={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                  }}
                >
                  Loading...
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<Index />} />

                {/* Legacy ProTradeOS/telecom URLs → redirect to home */}
                <Route path="/all-solutions" element={<Navigate to="/" replace />} />
                <Route path="/all-technology-solutions" element={<Navigate to="/" replace />} />
                <Route path="/communications-solutions" element={<Navigate to="/" replace />} />
                <Route path="/software-as-a-service-saas" element={<Navigate to="/" replace />} />
                <Route path="/artificial-intelligence" element={<Navigate to="/" replace />} />
                <Route path="/managed-services" element={<Navigate to="/" replace />} />
                <Route path="/infrastructure-solutions" element={<Navigate to="/" replace />} />
                <Route path="/security-solutions" element={<Navigate to="/" replace />} />
                <Route path="/energy-solutions" element={<Navigate to="/" replace />} />
                <Route path="/hardware-and-logistics" element={<Navigate to="/" replace />} />
                <Route path="/field-communications" element={<Navigate to="/" replace />} />
                <Route path="/failover-solutions" element={<Navigate to="/" replace />} />
                <Route path="/fixed-wireless-solutions" element={<Navigate to="/" replace />} />
                <Route path="/business-internet-solutions" element={<Navigate to="/" replace />} />
                <Route path="/internet-of-things-iot" element={<Navigate to="/" replace />} />
                <Route path="/pots-replacement-solutions" element={<Navigate to="/" replace />} />
                <Route path="/pots-replacement" element={<Navigate to="/" replace />} />
                <Route path="/starlink-business-internet" element={<Navigate to="/" replace />} />
                <Route path="/starlink-not-available" element={<Navigate to="/" replace />} />
                <Route path="/home-internet" element={<Navigate to="/" replace />} />
                <Route path="/internet-outside-city-limits" element={<Navigate to="/" replace />} />
                <Route path="/business-core" element={<Navigate to="/" replace />} />
                <Route path="/business-advanced" element={<Navigate to="/" replace />} />
                <Route path="/business-failover" element={<Navigate to="/" replace />} />
                <Route path="/business-ultimate" element={<Navigate to="/" replace />} />
                <Route path="/core-failover" element={<Navigate to="/" replace />} />
                <Route path="/advanced-failover" element={<Navigate to="/" replace />} />
                <Route path="/mission-critical" element={<Navigate to="/" replace />} />
                <Route path="/enterprise-shield" element={<Navigate to="/" replace />} />
                <Route path="/fleet-asset-tracking" element={<Navigate to="/" replace />} />
                <Route path="/asset-tracking-solutions" element={<Navigate to="/" replace />} />
                <Route path="/asset-tracking-intake" element={<Navigate to="/" replace />} />
                <Route path="/field-tablets-mobile-internet" element={<Navigate to="/" replace />} />
                <Route path="/jobsite-connectivity" element={<Navigate to="/" replace />} />
                <Route path="/push-to-talk" element={<Navigate to="/" replace />} />
                {/* Legacy form slugs → clean contractor order form */}
                <Route path="/gps-order-form" element={<Navigate to="/admin/order-form" replace />} />
                <Route path="/pots-order-form" element={<Navigate to="/admin/order-form" replace />} />
                <Route path="/peplink-quote-builder" element={<Navigate to="/admin/order-form" replace />} />
                <Route path="/gps-form" element={<Navigate to="/admin/order-form" replace />} />
                <Route path="/pots-form" element={<Navigate to="/admin/order-form" replace />} />
                <Route path="/peplink-form" element={<Navigate to="/admin/order-form" replace />} />
                <Route path="/admin/gps-order-form" element={<Navigate to="/admin/order-form" replace />} />
                <Route path="/admin/pots-order-form" element={<Navigate to="/admin/order-form" replace />} />
                <Route path="/admin/peplink-quote-builder" element={<Navigate to="/admin/order-form" replace />} />

                {/* Clean contractor URLs */}
                <Route path="/order-form" element={<Navigate to="/admin/order-form" replace />} />
                <Route path="/new-order-form" element={<Navigate to="/admin/order-form" replace />} />
                <Route path="/get-connected" element={<Navigate to="/contact-us" replace />} />
                <Route path="/preview/new-homepage" element={<Navigate to="/" replace />} />
                <Route path="/llm.txt" element={<Navigate to="/" replace />} />

                {/* Public pages */}
                <Route path="/about-us" element={<About />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/:slug" element={<ResourceDetail />} />
                <Route path="/contact-us" element={<Contact />} />
                <Route path="/become-a-partner" element={<BecomePartner />} />
                <Route path="/ai-quote" element={<AIQuote />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:slug" element={<ServicePage />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/template" element={<Template />} />

                {/* Legal */}
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/legal/end-user-terms" element={<EndUserTerms />} />
                <Route path="/legal/leasing-addendum" element={<LeasingAddendum />} />
                <Route path="/legal/camera-ai-addendum" element={<CameraAIAddendum />} />
                <Route path="/legal/alarm-fire-addendum" element={<AlarmFireAddendum />} />

                {/* Reschedule + customer-facing quote/sign */}
                <Route path="/reschedule/:appointmentId" element={<Reschedule />} />
                <Route path="/quotes/:id" element={<CustomerQuoteView />} />
                <Route path="/sign/:token" element={<SignAgreement />} />

                {/* Admin Login (Public) */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/reset-password" element={<AdminResetPassword />} />

                {/* Protected Admin Routes */}
               <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
               <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/contacts" element={<ProtectedRoute><AdminContacts /></ProtectedRoute>} />
                <Route path="/admin/contacts/:id" element={<ProtectedRoute><ContactDetail /></ProtectedRoute>} />
                <Route path="/admin/appointments" element={<ProtectedRoute><AppointmentsCalendar /></ProtectedRoute>} />
                <Route path="/admin/appointments/list" element={<ProtectedRoute><AppointmentsList /></ProtectedRoute>} />
                <Route path="/admin/calendar" element={<ProtectedRoute><AdminCalendar /></ProtectedRoute>} />
                <Route path="/admin/appointments/:id" element={<ProtectedRoute><AppointmentDetail /></ProtectedRoute>} />
                <Route path="/admin/activities" element={<ProtectedRoute><Activities /></ProtectedRoute>} />
                <Route path="/admin/user-approvals" element={<ProtectedRoute><UserApprovals /></ProtectedRoute>} />
                <Route path="/admin/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
                <Route path="/admin/resources" element={<ProtectedRoute><AdminResources /></ProtectedRoute>} />
                <Route path="/admin/resources/new" element={<ProtectedRoute><AdminResourceEditor /></ProtectedRoute>} />
                <Route path="/admin/resources/edit/:id" element={<ProtectedRoute><AdminResourceEditor /></ProtectedRoute>} />
                <Route path="/admin/quotes" element={<ProtectedRoute><AdminQuotes /></ProtectedRoute>} />
                <Route path="/admin/quotes/new" element={<ProtectedRoute><AdminQuoteBuilder /></ProtectedRoute>} />
                <Route path="/admin/quotes/:id" element={<ProtectedRoute><AdminQuoteDetail /></ProtectedRoute>} />
                <Route path="/admin/quotes/:id/edit" element={<ProtectedRoute><AdminQuoteBuilder /></ProtectedRoute>} />
                <Route path="/admin/products" element={<ProtectedRoute><AdminProducts /></ProtectedRoute>} />
                <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
                <Route path="/admin/chat" element={<ProtectedRoute><AdminChat /></ProtectedRoute>} />
                <Route path="/admin/tasks" element={<ProtectedRoute><AdminTasks /></ProtectedRoute>} />
                <Route path="/admin/agreements" element={<ProtectedRoute><AdminAgreements /></ProtectedRoute>} />
                <Route path="/admin/order-form" element={<ProtectedRoute><AdminOrderForm /></ProtectedRoute>} />
                <Route path="/admin/reseller-orgs" element={<ProtectedRoute><AdminResellerOrgs /></ProtectedRoute>} />
                <Route path="/admin/reseller-orgs/:id" element={<ProtectedRoute><AdminResellerOrgDetail /></ProtectedRoute>} />
                <Route path="/admin/ai-quotes" element={<ProtectedRoute><AdminAIQuotes /></ProtectedRoute>} />
                <Route path="/admin/ai-quotes/:id" element={<ProtectedRoute><AdminAIQuoteDetail /></ProtectedRoute>} />
                <Route path="/admin/trade-mode" element={<ProtectedRoute><AdminTradeMode /></ProtectedRoute>} />
                <Route path="/admin/onboarding" element={<ProtectedRoute><AdminOnboarding /></ProtectedRoute>} />
                <Route path="/admin/ai-feedback" element={<ProtectedRoute><AdminAIFeedback /></ProtectedRoute>} />
                <Route path="/admin/assistant" element={<ProtectedRoute><AdminAIAssistant /></ProtectedRoute>} />

                {/* Reseller Portal */}
                <Route path="/portal" element={<Navigate to="/portal/dashboard" replace />} />
                <Route path="/portal/login" element={<PortalLogin />} />
                <Route path="/portal/dashboard" element={<PortalDashboard />} />
                <Route path="/portal/organization" element={<PortalOrganization />} />
                <Route path="/portal/locations" element={<PortalLocations />} />
                <Route path="/portal/lines" element={<PortalLines />} />
                <Route path="/portal/tickets" element={<PortalTickets />} />
                <Route path="/portal/users" element={<PortalUsers />} />

                {/* Legacy POTS portal redirects */}
                <Route path="/pots/portal" element={<Navigate to="/portal/dashboard" replace />} />
                <Route path="/pots/portal/*" element={<Navigate to="/portal/dashboard" replace />} />

                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </AnalyticsProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
